import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';

const PANVerificationScreen = ({ verificationStatus, onStatusUpdate }) => {
    const navigate = useNavigate();
    const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
    const userInfo = JSON.parse(localStorage.getItem('tempUserInfo') || '{}');

    const [documentName, setDocumentName] = useState(userInfo.name || '');
    const [panImage, setPanImage] = useState(null);
    const [panImagePreview, setPanImagePreview] = useState(null);
    const [loading, setLoading] = useState(false);

    const isVerified = verificationStatus?.pan?.verified || false;
    const isPending =
        verificationStatus?.pan?.pending ||
        verificationStatus?.pan?.photoSubmitted ||
        false;
    const verifiedAt = verificationStatus?.pan?.verifiedAt;

    const handleImageUpload = (event) => {
        const file = event.target.files[0];
        if (!file) return;
        if (file.size > 5 * 1024 * 1024) {
            toast.error('Image size should be less than 5MB');
            return;
        }
        setPanImage(file);
        const reader = new FileReader();
        reader.onload = (loadEvent) => setPanImagePreview(loadEvent.target.result);
        reader.readAsDataURL(file);
    };

    const convertToBase64 = (file) =>
        new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result);
            reader.onerror = (err) => reject(err);
        });

    const handleSubmit = async () => {
        if (!documentName.trim()) {
            toast.error('Please enter your full name');
            return;
        }
        if (!panImage) {
            toast.error('Please upload a photo of your PAN card');
            return;
        }

        setLoading(true);
        try {
            const frontImageBase64 = await convertToBase64(panImage);

            await axios.post(`${baseUrl}/idfy/upload-kyc-document`, {
                userId: userInfo.id,
                documentType: 'pan',
                name: documentName.trim(),
                frontImageBase64,
            });

            toast.success(
                'PAN card uploaded. An admin will review and approve your verification shortly.',
            );
            if (onStatusUpdate) onStatusUpdate();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Upload failed. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    if (!userInfo.id) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-100 flex items-center justify-center p-4">
                <div className="text-center">
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">Session Expired</h2>
                    <p className="text-gray-600 mb-4">Please login again to continue verification</p>
                    <button
                        onClick={() => navigate('/login')}
                        className="bg-cm-gold text-cm-gold-ink px-6 py-2 rounded-lg hover:bg-cm-gold-hover"
                    >
                        Go to Login
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-100 flex items-center justify-center p-4">
            <div className="max-w-md w-full space-y-6">
                <div className="text-center">
                    <h2 className="text-3xl font-bold text-gray-900">PAN Verification</h2>
                    <p className="mt-2 text-sm text-gray-600">
                        {isVerified
                            ? 'Your PAN has been verified'
                            : isPending
                                ? 'Your document is under admin review'
                                : 'Upload a photo of your PAN card'}
                    </p>
                </div>

                <div className="bg-white py-8 px-6 shadow-xl rounded-2xl">
                    {isVerified ? (
                        <div className="text-center p-6 bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl border border-green-200">
                            <div className="mx-auto h-16 w-16 flex items-center justify-center rounded-full bg-gradient-to-r from-green-500 to-emerald-600 shadow-lg mb-4">
                                <svg className="h-8 w-8 text-white" fill="currentColor" viewBox="0 0 20 20">
                                    <path
                                        fillRule="evenodd"
                                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                        clipRule="evenodd"
                                    />
                                </svg>
                            </div>
                            <h3 className="text-xl font-bold text-green-900 mb-2">PAN Verified</h3>
                            <p className="text-sm text-gray-600 mb-2">
                                Verified on: {verifiedAt ? new Date(verifiedAt).toLocaleDateString() : 'Recently'}
                            </p>
                            <span className="inline-block px-3 py-1 bg-green-100 text-green-800 text-xs font-semibold rounded-full">
                                Approved
                            </span>
                        </div>
                    ) : isPending ? (
                        <div className="text-center p-6 bg-gradient-to-br from-amber-50 to-yellow-50 rounded-xl border border-amber-200">
                            <div className="mx-auto h-16 w-16 flex items-center justify-center rounded-full bg-gradient-to-r from-amber-400 to-yellow-500 shadow-lg mb-4">
                                <svg className="h-8 w-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                                    />
                                </svg>
                            </div>
                            <h3 className="text-xl font-bold text-amber-900 mb-2">Under Admin Review</h3>
                            <p className="text-sm text-amber-800 mb-4">
                                Your PAN card has been submitted. An admin will review and approve your verification shortly.
                            </p>
                            <span className="inline-block px-3 py-1 bg-amber-100 text-amber-800 text-xs font-semibold rounded-full">
                                Pending Approval
                            </span>
                        </div>
                    ) : (
                        <div className="space-y-5">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Full Name <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    value={documentName}
                                    onChange={(event) => setDocumentName(event.target.value)}
                                    placeholder="Enter your full name"
                                    className="w-full rounded-lg border border-gray-300 px-4 py-3 focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    PAN Card Photo <span className="text-red-500">*</span>
                                </label>
                                <div className="flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-lg hover:border-green-400 transition-colors">
                                    <div className="space-y-1 text-center">
                                        {panImagePreview ? (
                                            <div className="mb-2">
                                                <img
                                                    src={panImagePreview}
                                                    alt="PAN Preview"
                                                    className="mx-auto h-32 w-auto rounded-lg shadow-md object-contain"
                                                />
                                                <button
                                                    onClick={() => {
                                                        setPanImage(null);
                                                        setPanImagePreview(null);
                                                    }}
                                                    className="mt-2 text-sm text-red-600 hover:text-red-500"
                                                >
                                                    Remove
                                                </button>
                                            </div>
                                        ) : (
                                            <>
                                                <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                                                    <path
                                                        d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                                                        strokeWidth={2}
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                    />
                                                </svg>
                                                <div className="flex text-sm text-gray-600 justify-center">
                                                    <label htmlFor="pan-upload" className="cursor-pointer font-medium text-green-600 hover:text-green-500">
                                                        <span>Upload PAN photo</span>
                                                        <input
                                                            id="pan-upload"
                                                            type="file"
                                                            accept="image/*"
                                                            className="sr-only"
                                                            onChange={handleImageUpload}
                                                        />
                                                    </label>
                                                    <p className="pl-1">or drag and drop</p>
                                                </div>
                                                <p className="text-xs text-gray-500">PNG, JPG up to 5MB</p>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <button
                                onClick={handleSubmit}
                                disabled={loading || !panImage || !documentName.trim()}
                                className="w-full bg-cm-gold text-cm-gold-ink py-3 px-4 rounded-xl font-semibold hover:bg-cm-gold-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                            >
                                {loading ? (
                                    <span className="flex items-center justify-center">
                                        <svg className="animate-spin h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                                        </svg>
                                        Uploading...
                                    </span>
                                ) : (
                                    'Submit for Review'
                                )}
                            </button>
                        </div>
                    )}
                </div>

                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                    <div className="flex items-start">
                        <svg className="w-5 h-5 text-yellow-600 mt-0.5 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path
                                fillRule="evenodd"
                                d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                                clipRule="evenodd"
                            />
                        </svg>
                        <p className="text-xs text-yellow-800">
                            {isVerified
                                ? 'Your PAN is verified. Your account is now fully verified.'
                                : isPending
                                    ? 'Your document has been received. Once the admin approves it, your account will be marked as verified.'
                                    : 'Upload a clear photo of your PAN card and enter your name. No PAN number entry is required.'}
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PANVerificationScreen;
